import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getUsername } from '../../utils/indexedDB/indexedDB';
import { initElasticRUM } from '../../services/elasticRUM';

type User = {
  id: number | undefined;
  displayName: string;
  isExpert: boolean;
  photoURL: string | undefined;
  auth_token: string | undefined;
  sib_int_organization_id: number | undefined;
};

type ExpertUserResponse = {
  id: number;
  first_name: string;
  avatar: string;
  auth_token: string;
  sib_int_organization_id: number;
};

export default function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const headers = new window.Headers();
    headers.set('application_hash', import.meta.env.VITE_APPLICATION_HASH as string);
    const endpoint = ((import.meta.env.VITE_API_URL as string) + import.meta.env.VITE_AUTH_EXPERT_ENDPOINT) as string;

    fetch(endpoint, { headers, credentials: 'include' })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Expert auth failed');
        }
        return res.json();
      })
      .then((res: ExpertUserResponse) => {
        setUser({
          id: res.id,
          displayName: res.first_name as string,
          isExpert: true,
          photoURL: res.avatar || undefined,
          auth_token: res.auth_token,
          sib_int_organization_id: res.sib_int_organization_id,
        });
        setIsAuthReady(true);

        if (import.meta.env.MODE === 'staging' || import.meta.env.MODE === 'production') {
          initElasticRUM(res.auth_token, res.sib_int_organization_id);
        }
      })
      .catch(() => {
        getUsername().then((username) => {
          setUser({
            id: undefined,
            displayName: username as string,
            isExpert: false,
            photoURL: undefined,
            auth_token: undefined,
            sib_int_organization_id: undefined,
          });
          setIsAuthReady(true);
        });
      });
  }, [location]);

  const getToken = async (identity: string, slug: string, room: string) => {
    const headers = new window.Headers();
    headers.set('application_hash', import.meta.env.VITE_APPLICATION_HASH as string);

    const params = new window.URLSearchParams({ identity, slug, appointment_hash: room });

    const endpoint = user?.isExpert
      ? (((import.meta.env.VITE_API_URL as string) + import.meta.env.VITE_TOKEN_EXPERT_SCHEDULED_ENDPOINT) as string)
      : (((import.meta.env.VITE_API_URL as string) + import.meta.env.VITE_TOKEN_SCHEDULED_ENDPOINT) as string);

    const fetchOptions: RequestInit = user?.isExpert ? { headers, credentials: 'include' } : { headers };

    return fetch(`${endpoint}?${params}`, fetchOptions).then((res) => {
      return res.json().then((data) => {
        if (!res.ok) {
          return Promise.reject(new Error(data?.message));
        }
        return data;
      });
    });
  };

  return { user, isAuthReady, getToken };
}
